/** Thói quen — `S.habits` của bản mẫu (spec 15): tab Tuần này / Theo tháng, thêm trong hộp, xoá có hỏi lại. */
import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Habit as HabitSchema, ACTIVITIES } from "@ca/shared";
import { Button, Card, CardHead, Chip, Modal, PageHead, Ring, SpaceTag, Tabs } from "@ca/ui";
import { api } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { plusDay } from "./planner.tsx";
import { ACT } from "./charts.tsx";

type Habit = z.infer<typeof HabitSchema>;
const SHORT_DAYS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const AUTO_MIN = 10;
const actName = (id: string) => ACT.find((a) => a.id === id)?.name ?? id;
const monthOf = (date: string) => date.slice(0, 7);
const shiftMonth = (month: string, n: number) => {
  const d = new Date(Number(month.slice(0, 4)), Number(month.slice(5)) - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const daysIn = (month: string) =>
  new Date(Number(month.slice(0, 4)), Number(month.slice(5)), 0).getDate();

function useHabits(uid: string, months: string[]) {
  const results = useQueries({
    queries: months.map((month) => ({
      queryKey: ["habits", uid, month],
      queryFn: async () =>
        z.array(HabitSchema).parse(await api(`/v1/me/habits?month=${month}`, { accountId: uid })),
    })),
  });
  const list = new Map<string, Habit>();
  for (const r of results)
    for (const h of r.data ?? []) {
      const prev = list.get(h.id);
      list.set(h.id, prev ? { ...prev, checks: [...new Set([...prev.checks, ...h.checks])] } : h);
    }
  return {
    list: [...list.values()],
    pending: results.some((r) => r.isPending),
    error: results.find((r) => r.error)?.error,
  };
}

export function HabitsPage({
  uid,
  date,
  notify,
}: {
  uid: string;
  date: string;
  notify: (s: string) => void;
}) {
  const cache = useQueryClient();
  const [tab, setTab] = useState<"week" | "month">("week"),
    [month, setMonth] = useState(monthOf(date)),
    [adding, setAdding] = useState(false),
    [removing, setRemoving] = useState<Habit | null>(null),
    [busy, setBusy] = useState(false);
  const monday = plusDay(date, -((new Date(`${date}T00:00:00`).getDay() + 6) % 7));
  const week = Array.from({ length: 7 }, (_, i) => plusDay(monday, i));
  const months = tab === "week" ? [...new Set(week.map(monthOf))] : [month];
  const habits = useHabits(uid, months);
  const list = habits.list;
  const refresh = () =>
    Promise.all([
      cache.invalidateQueries({ queryKey: ["habits", uid] }),
      cache.invalidateQueries({ queryKey: ["today", uid] }),
    ]);
  const tick = (h: Habit, checked: boolean) => {
    setBusy(true);
    void api(`/v1/me/habits/${h.id}/checks/${date}`, {
      method: "PUT",
      accountId: uid,
      body: { checked },
    })
      .then(refresh)
      .catch((err: unknown) => {
        notify(String(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const label = (h: Habit) => (
    <div className="hn">
      <span className="hn-t" title={h.name}>
        {h.icon ? `${h.icon} ` : ""}
        {h.name}
      </span>
      {h.autoActivity ? (
        <span
          className="auto"
          data-tip={`Cá tự tick khi bạn học ${actName(h.autoActivity)} đủ ${AUTO_MIN} phút trong ngày`}
        >
          tự tick
        </span>
      ) : (
        <span className="hand" data-tip="Tick tay|Bấm vào ô hôm nay để tick">
          tick tay
        </span>
      )}
      <button
        className="hdel"
        aria-label={`Xoá thói quen ${h.name}`}
        data-tip={`Xoá thói quen|${h.name}`}
        onClick={() => {
          setRemoving(h);
        }}
      >
        🗑
      </button>
    </div>
  );
  const cell = (h: Habit, d: string, style?: CSSProperties) => {
    const checked = h.checks.includes(d),
      fut = d > date,
      today = d === date,
      cls = `hb${checked ? (h.autoActivity ? " auto" : " on") : ""}${fut ? " fut" : ""}${today ? " t" : ""}`;
    if (today && !h.autoActivity)
      return (
        <button
          key={d}
          className={`${cls} hb-tick`}
          role="checkbox"
          aria-checked={checked}
          aria-label="Hôm nay"
          title={`Tick hôm nay: ${h.name}`}
          disabled={busy}
          style={style}
          data-tip={checked ? "Bỏ tick hôm nay" : "Tick hôm nay"}
          onClick={() => {
            tick(h, !checked);
          }}
        >
          {checked ? "✓" : ""}
        </button>
      );
    return (
      <div
        key={d}
        className={cls}
        style={style}
        data-tip={
          checked
            ? h.autoActivity
              ? "Cá tự tick từ hoạt động thật"
              : "Bạn tick tay"
            : fut
              ? "Chưa tới"
              : "Chưa làm"
        }
      >
        {checked ? "✓" : ""}
      </div>
    );
  };
  const empty = (
    <p className="muted" style={{ fontSize: 13.5, padding: "10px 0" }}>
      Chưa có thói quen nào. Bấm “＋ Thêm thói quen” để bắt đầu.
    </p>
  );
  const addBtn = (
    <Button
      variant="acc"
      size="sm"
      onClick={() => {
        setAdding(true);
      }}
    >
      ＋ Thêm thói quen
    </Button>
  );
  const doneToday = list.filter((h) => h.checks.includes(date)).length;
  const mDays = Array.from(
    { length: daysIn(month) },
    (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`,
  );
  let done = 0,
    all = 0;
  if (tab === "month")
    for (const h of list)
      for (const d of mDays)
        if (d <= date) {
          all++;
          if (h.checks.includes(d)) done++;
        }
  return (
    <>
      <PageHead
        space="prod"
        title="Thói quen"
        desc="Đánh dấu những việc nhỏ mỗi ngày. Thói quen gắn với việc học được Cá tự tick khi bạn làm thật."
      >
        <Tabs
          label="Xem thói quen"
          items={[
            { key: "week", label: "Tuần này" },
            { key: "month", label: "Theo tháng" },
          ]}
          active={tab}
          onChange={setTab}
        />
      </PageHead>
      {tab === "week" ? (
        <div className="grid g-8-4">
          <Card>
            <CardHead
              title="Tuần này"
              sub="Ô tím: Cá tự tick từ việc bạn thật sự làm · ô đen: bạn tick tay · 🗑 để xoá một thói quen"
            >
              {addBtn}
            </CardHead>
            {list.length ? (
              <div className="tbl-wrap">
                <div
                  className="habit-grid"
                  style={{ gridTemplateColumns: "minmax(230px,1.9fr) repeat(7,minmax(34px,1fr))" }}
                >
                  <div />
                  {week.map((d) => (
                    <div key={d} className={`dh${d === date ? " t" : ""}`}>
                      {SHORT_DAYS[new Date(`${d}T00:00:00`).getDay()]}
                      <br />
                      {d.slice(8, 10)}
                    </div>
                  ))}
                  {list.map((h) => (
                    <HabitRow key={h.id}>
                      {label(h)}
                      {week.map((d) => cell(h, d))}
                    </HabitRow>
                  ))}
                </div>
              </div>
            ) : habits.pending ? null : (
              empty
            )}
          </Card>
          <Card className="stack">
            <h3>Hôm nay</h3>
            <div className="row" style={{ gap: 14 }}>
              <div style={{ width: 96, height: 96 }}>
                <Ring
                  pct={list.length ? (doneToday / list.length) * 100 : 0}
                  sw={12}
                  color="var(--accent-2)"
                />
              </div>
              <div>
                <b style={{ fontSize: 28 }}>
                  {doneToday}/{list.length}
                </b>
                <p className="muted" style={{ fontSize: 13 }}>
                  thói quen xong hôm nay
                </p>
              </div>
            </div>
            <p className="muted" style={{ fontSize: 13 }}>
              Thói quen “tự tick” được Cá đánh dấu khi bạn học đủ {AUTO_MIN} phút đúng việc đó, bạn
              không phải nhớ quay lại để tick.
            </p>
          </Card>
        </div>
      ) : (
        <Card>
          <div className="card-head">
            <div className="row" style={{ gap: 6 }}>
              <Button
                variant="soft"
                size="sm"
                className="btn-icon"
                aria-label="Tháng trước"
                onClick={() => {
                  setMonth(shiftMonth(month, -1));
                }}
              >
                ‹
              </Button>
              <b>
                Th{Number(month.slice(5))}/{month.slice(0, 4)}
              </b>
              <Button
                variant="soft"
                size="sm"
                className="btn-icon"
                aria-label="Tháng sau"
                disabled={month >= monthOf(date)}
                onClick={() => {
                  setMonth(shiftMonth(month, 1));
                }}
              >
                ›
              </Button>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <Chip tone="dark">
                {Math.round((done / Math.max(1, all)) * 100)}% · tỉ lệ hoàn thành tháng
              </Chip>
              {addBtn}
            </div>
          </div>
          {list.length ? (
            <div className="tbl-wrap">
              <div
                className="habit-grid hg-month"
                style={{
                  gridTemplateColumns: `minmax(210px,1.6fr) repeat(${mDays.length},minmax(16px,1fr))`,
                  gap: 3,
                  minWidth: 900,
                }}
              >
                <div />
                {mDays.map((d) => (
                  <div key={d} className="dh">
                    {Number(d.slice(8))}
                  </div>
                ))}
                {list.map((h) => (
                  <HabitRow key={h.id}>
                    {label(h)}
                    {mDays.map((d) => cell(h, d, { borderRadius: 5 }))}
                  </HabitRow>
                ))}
              </div>
            </div>
          ) : habits.pending ? null : (
            empty
          )}
          <p className="faint" style={{ fontSize: 12.5, marginTop: 10, fontWeight: 600 }}>
            Bấm 🗑 cạnh tên để xoá một thói quen. Thói quen tick tay chỉ tick được trong ngày hôm
            nay.
          </p>
        </Card>
      )}
      {habits.error && <p role="alert">{String(habits.error)}</p>}
      {adding && (
        <AddHabit
          uid={uid}
          notify={notify}
          onDone={() => {
            setAdding(false);
            void refresh();
          }}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      {removing && (
        <Modal
          title="Xoá thói quen"
          className="sm"
          head={<SpaceTag space="prod" />}
          onClose={() => {
            setRemoving(null);
          }}
        >
          <h2>Bạn có chắc muốn xoá vĩnh viễn không?</h2>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            “{removing.name}” sẽ bị xoá hẳn, không lấy lại được.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setRemoving(null);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                const h = removing;
                void api(`/v1/me/habits/${h.id}`, { method: "DELETE", accountId: uid })
                  .then(async () => {
                    setRemoving(null);
                    notify(`Đã xoá thói quen “${h.name}”`);
                    await refresh();
                  })
                  .catch((err: unknown) => {
                    notify(String(err));
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
            >
              🗑 Xoá vĩnh viễn
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
/** Các ô của một hàng nằm thẳng trong lưới `.habit-grid` (không tạo thẻ bọc). */
function HabitRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
function AddHabit({
  uid,
  notify,
  onDone,
  onClose,
}: {
  uid: string;
  notify: (s: string) => void;
  onDone: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(""),
    [auto, setAuto] = useState(""),
    [busy, setBusy] = useState(false),
    id = useRef(uuidv7());
  const save = () => {
    if (!name.trim()) {
      notify("Nhập tên thói quen trước nhé");
      return;
    }
    setBusy(true);
    void api(`/v1/me/habits/${id.current}`, {
      method: "PUT",
      accountId: uid,
      body: { name: name.trim(), autoActivity: auto || null },
    })
      .then(() => {
        notify(`Đã thêm thói quen “${name.trim()}”`);
        onDone();
      })
      .catch((err: unknown) => {
        notify(String(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <Modal
      title="＋ Thêm thói quen"
      className="sm"
      hideTitle
      head={<SpaceTag space="prod" />}
      onClose={onClose}
    >
      <h2>＋ Thêm thói quen</h2>
      <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 14px" }}>
        Đặt tên ngắn gọn rồi chọn cách tick. Thói quen mới bắt đầu tính từ hôm nay.
      </p>
      <div className="stack" style={{ gap: 14 }}>
        <div className="field">
          <label htmlFor="haName">Tên thói quen</label>
          <input
            id="haName"
            className="input"
            maxLength={100}
            autoFocus
            autoComplete="off"
            placeholder="Ví dụ: Đọc 1 đoạn Part 7 trước khi ngủ"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
          />
        </div>
        <div className="field">
          <label id="haHow">Cách tick</label>
          <div className="stack" role="radiogroup" aria-labelledby="haHow" style={{ gap: 6 }}>
            {[
              ["", "Tự tay tick mỗi ngày"] as const,
              ...ACTIVITIES.filter((a) => a !== "other").map(
                (a) => [a, `Cá tự tick khi học ${actName(a)} đủ ${AUTO_MIN} phút`] as const,
              ),
            ].map(([value, text]) => (
              <button
                key={value || "hand"}
                className="opt"
                role="radio"
                aria-checked={auto === value}
                style={{ padding: "10px 14px" }}
                onClick={() => {
                  setAuto(value);
                }}
              >
                <span>
                  <b style={{ fontSize: 13.5 }}>{text}</b>
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <Button variant="soft" onClick={onClose}>
            Huỷ
          </Button>
          <Button variant="acc" disabled={busy} onClick={save}>
            ＋ Thêm thói quen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
